/**
 * CASL Ability Definitions
 * Defines permissions for each role based on the requirements
 */

import { AbilityBuilder, PureAbility } from '@casl/ability';
import { UserRole, Action, Subject } from './types';

export type AppAbility = PureAbility<[Action, Subject]>;

/**
 * Base abilities that ALL authenticated users have
 * These are common to every role (student, professor, admin, etc.)
 */
const defineBaseAbilities = (builder: AbilityBuilder<AppAbility>) => {
  const { can } = builder;

  can('access', 'Dashboard');
  can('access', 'Calendar');
  can('read', 'Degree');
  can('read', 'Lesson');
  can('create', 'Message');
  can('read', 'Message');
};

/**
 * Abilities for staff members (Professor, Counselor, Transcript Evaluator, Admins)
 * These are shared by all non-student roles
 */
const defineStaffAbilities = (builder: AbilityBuilder<AppAbility>) => {
  const { can } = builder;

  can('create', 'Note');
  can('read', 'Note');
  can('access', 'Chat');
};

/**
 * Abilities for academic staff (Professor, Platform Admin)
 * These are shared by roles that work with academic content
 */
const defineAcademicStaffAbilities = (builder: AbilityBuilder<AppAbility>) => {
  const { can } = builder;

  can('access', 'Schedule');
  can('access', 'Course');
  can('read', 'Course');
  can('read', 'Objective');
  can('read', 'Assessment');
  can('access', 'CourseObjectivesOverview');
  can('access', 'CourseProjectTab');
};

/**
 * Super Admin - Emergency, break-glass authority
 * Full access to everything on the platform
 */
const defineSuperAdminAbilities = (builder: AbilityBuilder<AppAbility>) => {
  const { can } = builder;

  can('manage', 'all');
};

/**
 * Platform Admin - Institutional and operational authority
 */
const definePlatformAdminAbilities = (builder: AbilityBuilder<AppAbility>) => {
  const { can } = builder;

  defineBaseAbilities(builder);
  defineStaffAbilities(builder);
  defineAcademicStaffAbilities(builder);

  can('create', 'Degree');
  can('access', 'Degree');
  can('access', 'DegreeList');
  can('update', 'Degree');
  can('publish', 'Degree');
  can('archive', 'Degree');
  can('assign', 'Degree');
  can('create', 'Course');
  can('update', 'Course');
  can('publish', 'Course');
  can('archive', 'Course');
  can('assign', 'Course');
  can('create', 'Lesson');
  can('update', 'Lesson');
  can('create', 'Objective');
  can('update', 'Objective');
  can('manage', 'User');
  can('manage', 'Role');
  can('manage', 'Permission');
  can('manage', 'Account');
  can('manage', 'Billing');
  can('manage', 'Integration');
  can('manage', 'Analytics');
  can('manage', 'Feature');
  can('override', 'Grade');
  can('override', 'Assessment');
  can('override', 'Transcript');
  can('read', 'Grade');
  can('read', 'Transcript');
  can('read', 'Feedback');
  can('create', 'Feedback');
  can('access', 'AdminPanel');
  can('access', 'Settings');
  can('access', 'Analytics');
  can('read', 'Course');
  can('access', 'SearchBar');
  can('read', 'Student');
  can('read', 'Announcement');
  can('create', 'Announcement');
  can('update', 'Announcement');
  can('manage', 'Announcement');
  can('manage', 'CourseHearing');
  can('access', 'ProjectsReview');
  can('manage', 'ProjectsReview');
  can('access', 'TranscriptsReview');
  can('manage', 'TranscriptsReview');
};

/**
 * Professor - Primary academic executor
 */
const defineProfessorAbilities = (builder: AbilityBuilder<AppAbility>) => {
  const { can } = builder;

  defineBaseAbilities(builder);
  defineStaffAbilities(builder);
  defineAcademicStaffAbilities(builder);
  can('grade', 'Project');
  can('read', 'Project');
  can('access', 'ProjectsReview');
  can('update', 'Assessment');
  can('access', 'CourseHearing');
  can('manage', 'CourseHearing');
};

/**
 * Transcript Evaluator - Official transfer-credit authority
 */
const defineTranscriptEvaluatorAbilities = (builder: AbilityBuilder<AppAbility>) => {
  const { can } = builder;

  defineBaseAbilities(builder);
  defineStaffAbilities(builder);

  can('read', 'Transcript');
  can('evaluate', 'Transcript');
  can('access', 'TranscriptsReview');
  can('approve', 'TransferCredit');
  can('create', 'TransferCredit');
  can('update', 'TransferCredit');
};

/**
 * Counselor - Student guidance and support
 */
const defineCounselorAbilities = (builder: AbilityBuilder<AppAbility>) => {
  const { can } = builder;

  defineBaseAbilities(builder);
  defineStaffAbilities(builder);

  can('read', 'StudentProfile');
  can('update', 'StudentProfile');
  can('read', 'Transcript');
  can('manage', 'Counseling');
};

/**
 * Student - Learner and community participant
 */
const defineStudentAbilities = (
  builder: AbilityBuilder<AppAbility>,
  enrolledDegreeIds: number[] = [],
  isOnboarded = false,
  isMaster = false
) => {
  const { can } = builder;

  defineBaseAbilities(builder);

  can('read', 'Objective');
  can('read', 'Assessment');
  can('create', 'Project');
  can('read', 'Project');
  can('update', 'Project');
  can('read', 'Grade');
  can('read', 'StudentProfile');
  can('update', 'StudentProfile');
  can('read', 'PublicProfile');
  can('read', 'Transcript');

  can('read', 'Degree');
  can('enroll', 'Degree');

  if (!isMaster) {
    can('access', 'DegreeList');
  }

  if (enrolledDegreeIds.length === 0) {
    can('access', 'Degree');
  }

  if (!isOnboarded) {
    can('access', 'Onboarding');
  }

  // Can work with courses if enrolled in at least one degree
  if (enrolledDegreeIds.length > 0) {
    can('read', 'Course');
    can('start', 'Course');
    can('pause', 'Course');
    can('resume', 'Course');
    can('access', 'Chat');
  }
};

/**
 * Main function to define abilities based on user role
 */
export const defineAbilitiesFor = (
  role: UserRole,
  enrolledDegreeIds: number[] = [],
  isOnboarded = false,
  isMaster = false
): AppAbility => {
  const builder = new AbilityBuilder<AppAbility>(PureAbility);

  switch (role) {
    case 'super_admin':
      defineSuperAdminAbilities(builder);
      break;
    case 'platform_admin':
      definePlatformAdminAbilities(builder);
      break;
    case 'professor':
      defineProfessorAbilities(builder);
      break;
    case 'transcript_evaluator':
      defineTranscriptEvaluatorAbilities(builder);
      break;
    case 'counselor':
      defineCounselorAbilities(builder);
      break;
    case 'student':
      defineStudentAbilities(builder, enrolledDegreeIds, isOnboarded, isMaster);
      break;
    default:
      break;
  }

  return builder.build();
};
